import * as React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, Grid, IconButton, InputAdornment } from '@mui/material';
import { CredentialResponse, GoogleLogin } from '@react-oauth/google';
import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../hooks/AuthProvider';
import { useTheme } from '../../hooks/ThemeProvider';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { isValidEmail, isValidPassword } from '../../lib/utils';
import { useSnackbar } from 'src/hooks/SnackbarProvider';
import './Login.scss';
import BrandNameAnimation from '../CanvasAnimations/BrandNameAnimation';
import { usePlan } from 'src/hooks/PlanProvider';
import { AuthAnalytics } from '../../lib/analytics';

/**
 * Renders a login component with options for email/password login and Google sign-in.
 * The component has two states: expanded (email/password form) and collapsed (Google sign-in).
 */
export default function Login() {
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { setSelectedPlanDefault } = usePlan();
  const { triggerSnackbar } = useSnackbar();
  const { isDarkMode } = useTheme();

  const redirectPath = location.state?.from || "/";

  const responseMessage = async (response: CredentialResponse) => {
    try {
      await auth.handleSocialAuthTokenExchange(response)
      AuthAnalytics.login('google_oauth', true);
      setSelectedPlanDefault();
      navigate(redirectPath, { replace: true });
    } catch (e) {
      AuthAnalytics.login('google_oauth', false);
      triggerSnackbar("Social authentication failed; you may not be on the allow-list during the beta phase.", 3000, "error")
    }
  };

  const errorMessage = () => {
    console.log("An unknown error occurred.");
  };

  const handleLoginExpandClick = () => setExpanded(!expanded)

  const handleLoginSubmitClick = async () => {
    try {
      setSubmitDisabled(true);
      await auth.handleLoginSubmit(email, password)
      AuthAnalytics.login('email_password', true);
      setSelectedPlanDefault();
      navigate(redirectPath, { replace: true });
    } catch {
      AuthAnalytics.login('email_password', false);
      triggerSnackbar("Login failed; try again.", 3000, "error")
      setSubmitDisabled(false);
    }
  };

  useEffect(() => {
    const isValid = isValidEmail(email) && isValidPassword(password)

    setSubmitDisabled(!isValid)
  }, [email, password])

  const handleLoginFormChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, formPart: string) => {
    switch (formPart) {
      case 'email': {
        setEmail(event.target.value)
        break;
      }
      case 'password': {
        setPassword(event.target.value)
        break;
      }
    }
  }

  if (expanded) {
    return (
      <div className="glassmorphic-card-container fade-in">
        <Card className="glassmorphic-card" sx={{ minWidth: 345 }}>
          <CardContent>
            <BrandNameAnimation />
            <Grid container padding={'12px;'} spacing={3}>
              <Grid item xs={12}>
                <TextField required fullWidth
                  label="Email"
                  inputProps={{ "data-testid": "login-email-input" }}
                  value={email}
                  onChange={(e) => handleLoginFormChange(e, 'email')}
                  className="slide-up"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth required
                  label="Password"
                  value={password}
                  type={showPassword ? "text" : "password"}
                  onChange={(e) => handleLoginFormChange(e, 'password')}
                  inputProps={{ "data-testid": "login-password-input" }}
                  className="slide-up"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => { setShowPassword((prev) => !prev) }}
                          onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}></Grid>
              <Grid item xs>
                <Button
                  data-testid="login-back-button"
                  onClick={() => handleLoginExpandClick()}
                  className="scale-in"
                >
                  Back
                </Button>
              </Grid>
              <Grid item xs={3}></Grid>
              <Grid item xs>
                <Button
                  variant="contained"
                  data-testid="login-submit-button"
                  disabled={submitDisabled}
                  onClick={() => handleLoginSubmitClick()}
                  className="gradient-primary scale-in"
                >
                  Submit
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </div>
    )
  } else {
    return (
      <div className="glassmorphic-card-container fade-in">
        <Card className="glassmorphic-card login" sx={{ maxWidth: 345 }}>
          <CardContent>
            <BrandNameAnimation />
            <Grid container spacing={2} direction="column" alignItems="center" justifyContent="center">
              <Grid item xs={12} className="slide-up">
                <div className="google-login-button">
                  <GoogleLogin
                    theme={isDarkMode ? "filled_black" : "outline"}
                    onSuccess={responseMessage}
                    onError={errorMessage}
                    auto_select={false}
                    text="signin_with"
                    shape="pill"
                    logo_alignment="center"
                  />
                </div>
              </Grid>
              <Grid item xs={12} className="slide-up">
                <Button
                  variant="contained"
                  onClick={() => navigate("/register")}
                  data-testid="register-button"
                  className="login-button gradient-primary"
                >
                  SIGN UP
                </Button>
              </Grid>
              <Grid item xs={12} className="slide-up">
                <Button
                  variant="outlined"
                  onClick={() => handleLoginExpandClick()}
                  data-testid="login-button"
                  className="login-button"
                >
                  EMAIL LOGIN
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </div>
    );
  }
}
